import {
    FulfillmentQuote,
    getProductSectionName,
    OrderFulfillmentProduct,
    useFulfillmentContext,
} from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { PRODUCT_GROUP_TYPE, PRODUCT_GROUP_TYPES, SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { Organization } from '@/clients/organization/types/organization.ts';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { useOrganizationName } from '@/clients/organization/useOrganization.ts';
import { z } from 'zod';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import { format, isValid as isValidDate, parseISO } from 'date-fns';
import { Price } from '@schuettflix/conversion';
import { useFulfillmentSectionsErrors } from '@/modules/fulfillment/hooks/useFulfillmentSectionsErrors.ts';
import { useSectionsValidity } from '@/modules/fulfillment/hooks/useSectionsValidity.ts';
import { Combobox, TextField } from '@schuettflix/react-components';
import { DatePickerField, NumberField } from '@schuettflix/planum-react';
import { Controller, FieldErrors, useWatch } from 'react-hook-form';
import { ReactHookFormDevTool } from '@/shared/components/ReactHookFormDevTool.tsx';
import { enrichHookFormErrorForFulfillmentSectionErrors } from '@/CustomRequestProductChannel/utils/enrichHookFormErrorForFulfillmentSectionErrors.ts';
import { useSupportedUnitNamesByProductType } from '@/CustomRequestProductChannel/hooks/useSupportedUnitNamesByProductType.ts';
import { getProductCategoryIcon } from '@/CustomRequestProductChannel/utils/getProductCategoryIcon.ts';
import {
    CustomRequestProductConfiguration,
    CustomRequestProductForm,
    FormErrors,
} from '@/CustomRequestProductChannel/components/CustomRequestProduct.tsx';
import { cn } from '@/shared/utils/cn.ts';
import { CustomRequestProductHints } from '@/CustomRequestProductChannel/components/CustomRequestProductHints.tsx';
import { EmitterSubscriber } from '@/shared/hooks/useEmitter';
import { useLineItemListingSync } from '@/modules/fulfillment/hooks/useLineItemListingSync';
import { useDeepCompareEffect } from 'react-use';
import { useFulfillmentDiffCheck } from '@/modules/fulfillment/hooks/useFulfillmentDiffCheck';
import { useActiveTaxClasses } from '@/clients/tax-classes/useTaxClasses.ts';
import { SelectOrganization } from '@/shared/components/SelectOrganization';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';
import { getLimitedDateRanges } from '@/shared/utils/getLimitedDateRanges.ts';
import { formatDateTimezoneOffsetHotfix } from '@/tools/hotfix/formatDateTimezoneOffsetHotfix.ts';

interface ProductItemProps {
    product: OrderFulfillmentProduct;
    orderingOrganization: Organization;
    hint?: string;
    groupType?: PRODUCT_GROUP_TYPE;
    disabled?: boolean;
    disabledFields?: Partial<Record<keyof Omit<CustomRequestProductForm, 'currencyCode'>, boolean>>;
    disableZeroPriceHints?: boolean;
    positionProductId: string | null;
    onPurchasePriceChange?: (purchasePrice: number) => void;
    onSalesPriceChange?: (salesPrice: number) => void;
    overrideFormErrors?: Omit<FormErrors, 'productName' | 'currencyCode'>;
    productConfiguration: CustomRequestProductConfiguration;
    onUpdateProductConfiguration?: (values: Partial<CustomRequestProductConfiguration>) => void;
    onIsDirtyChange: (isDirty: boolean) => void;
    resetEmitter?: EmitterSubscriber<'preset' | 'zero'>;
}

// fixme: this is a special implementation of the FulfillmentCustomRequestProduct component that can show the product from an order above the inputs
// fixme: align with FulfillmentCustomRequestProduct (combine) and use everywhere
export const FulfillmentCustomRequestProductForOrderedProduct: React.FC<ProductItemProps> = ({
    product,
    orderingOrganization,
    hint,
    groupType,
    disabled,
    disabledFields = {},
    disableZeroPriceHints,
    onPurchasePriceChange,
    onSalesPriceChange,
    overrideFormErrors,
    productConfiguration,
    onUpdateProductConfiguration,
    onIsDirtyChange,
    resetEmitter,
}) => {
    const {
        orderFulfillmentProducts,
        isSubmitted,
        canSubmit,
        setCanSubmit,
        updateOrderFulfillmentProduct,
        contextSource,
    } = useFulfillmentContext();
    const { t } = useTranslation();
    const { getCurrencySymbol } = useFormatters();
    const { data: supportedUnitNames } = useSupportedUnitNamesByProductType(product.type);
    const { data: taxClasses } = useActiveTaxClasses(orderingOrganization.market.code);

    const [currentFormErrors, setCurrentFormErrors] = useState<FormErrors>({});
    const formHasErrors = Object.keys(currentFormErrors).length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialImmutableOrderQuote = useMemo<FulfillmentQuote>(() => product.quote, []);

    const supportedUnitOptions = (supportedUnitNames ?? []).map(unit => {
        return {
            value: unit,
            label: t(SUPPORTED_UNIT_LABEL[unit]),
        };
    });

    const getFormError = (fieldName: keyof ProductItem) => {
        return currentFormErrors[fieldName];
    };

    const partnerOrganizationName = useOrganizationName(initialImmutableOrderQuote.partnerOrganizationId);

    const disallowNegativePriceSwitchingRuleProps = (immutableNumValue: number): [RegExp, string] =>
        immutableNumValue < 0
            ? [/^-.*/, t('product.fulfillment.errorMessages.switchToPositivePriceUnsupported')]
            : [/^(?!-).*/, t('product.fulfillment.errorMessages.switchToNegativePriceUnsupported')];

    const ProductItemSchema = z.object({
        name: z.string().min(1, t('product.fulfillment.errorMessages.name.required')),
        amount: z
            .number({ invalid_type_error: t('product.fulfillment.errorMessages.amount.required') })
            .min(0, t('product.fulfillment.errorMessages.amount.minValue')),
        unit: SUPPORTED_UNITS_SCHEMA,
        partnerOrganizationId: z
            .number()
            .int()
            .positive()
            .refine(id => !EXCLUDED_PARTNER_ORGANIZATION_IDS.includes(id), {
                message: t('forms.inputs.partnerSelectionInput.errorMessage.excluded'),
            }),
        platformOrganizationId: z.number().int().positive(),
        purchasePrice: z
            .string()
            .min(1, t('product.fulfillment.errorMessages.purchasePrice.required'))
            .regex(...disallowNegativePriceSwitchingRuleProps(initialImmutableOrderQuote.purchasePrice.toNumber())),
        purchaseTaxClassId: z.string({ message: t('product.fulfillment.errorMessages.purchaseTaxClassId.required') }),
        salesPrice: z
            .string()
            .min(1, t('product.fulfillment.errorMessages.salesPrice.required'))
            .regex(...disallowNegativePriceSwitchingRuleProps(initialImmutableOrderQuote.salesPrice.toNumber())),
        salesTaxClassId: z.string({ message: t('product.fulfillment.errorMessages.salesTaxClassId.required') }),
        serviceDate: z
            .string()
            .refine(date => isValidDate(parseISO(date)), t('product.fulfillment.errorMessages.serviceDate.invalid'))
            .refine(
                value => new Date(value) <= new Date(),
                t('product.fulfillment.errorMessages.serviceDate.backdating')
            ),
    });

    type ProductItem = z.infer<typeof ProductItemSchema>;
    type FormErrors = FieldErrors<ProductItem>;

    const {
        register,
        getValues,
        control,
        setValue,
        trigger: triggerFormValidation,
        formState: { errors, isValid, isDirty, defaultValues },
        setFocus,
        reset,
    } = useZodForm({
        schema: ProductItemSchema,
        defaultValues: {
            name: product.quote.name,
            amount: product.quote.amount,
            unit: product.quote.unit,
            partnerOrganizationId: product.quote.partnerOrganizationId ?? undefined,
            purchasePrice: product.quote.purchasePrice.toString(),
            purchaseTaxClassId: product.quote.purchaseTaxClassId,
            salesPrice: product.quote.salesPrice.toString(),
            salesTaxClassId: product.quote.salesTaxClassId,
            serviceDate: product.quote.serviceDate
                ? format(new Date(product.quote.serviceDate), 'yyyy-MM-dd')
                : undefined,
            platformOrganizationId: product.quote.platformOrganizationId,
        },
        mode: 'onChange',
    });

    const [productName, unit, newAmount] = useWatch({ control, name: ['name', 'unit', 'amount'] });
    useFulfillmentDiffCheck({
        productId: product.id,
        productName,
        unit,
        originalAmount: initialImmutableOrderQuote.amount,
        newAmount,
    });

    const { isLineItemListingActive, isServiceDateRange, serviceDateRangeForDatePicker } = useLineItemListingSync({
        productId: product.id,
        onAmountChange: amount => setValue('amount', amount, { shouldValidate: true }),
        onServiceDateRangeChange: serviceDateRange => {
            if (!serviceDateRange.length) return setValue('serviceDate', defaultValues?.serviceDate || '');
            setValue('serviceDate', format(serviceDateRange[1], 'yyyy-MM-dd'), { shouldValidate: true });
        },
    });

    useEffect(() => {
        onIsDirtyChange(isDirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    useEffect(
        () =>
            resetEmitter?.subscribe(() => {
                const values = getValues();
                reset({
                    ...values,

                    // only reset the fields that are not editable any longer...
                    amount: defaultValues?.amount,
                    serviceDate: defaultValues?.serviceDate,
                });
            }),
        [resetEmitter]
    );

    const handleChange = () => {
        const values = getValues();

        updateOrderFulfillmentProduct({
            id: product.id,
            quote: {
                id: product.quote.id,
                orderProductId: product.id,
                orderingOrganizationId: product.quote.orderingOrganizationId,
                currencyCode: product.quote.currencyCode,
                platformOrganizationId: product.quote.platformOrganizationId,
                purchaseTaxClassId: values.purchaseTaxClassId,
                salesTaxClassId: values.salesTaxClassId,
                purchasePrice: new Price(values.purchasePrice, product.quote.currencyCode),
                amount: values.amount,
                unit: values.unit,
                partnerOrganizationId: values.partnerOrganizationId,
                salesPrice: new Price(values.salesPrice, product.quote.currencyCode),
                serviceDate: values.serviceDate,
                name: values.name,
            },
        });
    };

    const mapPriceToNumber = (value: string) => {
        return new Price(value, orderingOrganization?.market.currencyCode ?? 'EUR').toNumber();
    };

    const values = {
        ...useWatch({ control }),
        ...getValues(),
    };
    useDeepCompareEffect(() => {
        handleChange();
    }, [values, {}]);

    const materialQuote = orderFulfillmentProducts.find(product => product.category === 'MATERIAL')?.quote;

    useEffect(() => {
        if (!isSubmitted) {
            return;
        }

        void triggerFormValidation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitted]);

    // sync transport product with leading product based on group type
    useEffect(() => {
        if (product.category !== 'TRANSPORT') {
            return;
        }

        switch (groupType) {
            case PRODUCT_GROUP_TYPES.FRANCO: {
                // bind fields to the leading product: amount, unit
                setValue('amount', materialQuote?.amount || 0, { shouldValidate: true });
                if (materialQuote?.unit) {
                    setValue('unit', materialQuote.unit, { shouldValidate: true });
                }
                break;
            }
            case PRODUCT_GROUP_TYPES.MERCHANT: {
                // bind fields to the leading product: amount, unit, product name, service date, tax classes, partner
                setValue('amount', materialQuote?.amount || 0, { shouldValidate: true });
                if (materialQuote?.unit) {
                    setValue('unit', materialQuote.unit, { shouldValidate: true });
                }
                setValue('name', materialQuote?.name || '', { shouldValidate: true });
                if (materialQuote?.serviceDate) {
                    setValue('serviceDate', format(new Date(materialQuote.serviceDate), 'yyyy-MM-dd'), {
                        shouldValidate: true,
                    });
                }
                if (materialQuote?.partnerOrganizationId) {
                    setValue('partnerOrganizationId', materialQuote.partnerOrganizationId, { shouldValidate: true });
                }
                if (materialQuote?.purchaseTaxClassId) {
                    setValue('purchaseTaxClassId', materialQuote.purchaseTaxClassId, { shouldValidate: true });
                }
                if (materialQuote?.salesTaxClassId) {
                    setValue('salesTaxClassId', materialQuote.salesTaxClassId, { shouldValidate: true });
                }
                break;
            }
            default:
                break;
        }

        handleChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        product.category,
        groupType,
        materialQuote?.amount,
        materialQuote?.unit,
        materialQuote?.name,
        materialQuote?.serviceDate,
        materialQuote?.partnerOrganizationId,
        materialQuote?.purchaseTaxClassId,
        materialQuote?.salesTaxClassId,
        setValue,
    ]);

    useEffect(() => {
        onPurchasePriceChange?.(mapPriceToNumber(getValues('purchasePrice')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getValues('purchasePrice')]);

    useEffect(() => {
        onSalesPriceChange?.(mapPriceToNumber(getValues('salesPrice')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getValues('salesPrice')]);

    useEffect(() => {
        if (formHasErrors && canSubmit) {
            setCanSubmit(false);
        }

        if (!formHasErrors && !canSubmit) {
            setCanSubmit(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formHasErrors]);

    useEffect(() => {
        setCurrentFormErrors({
            ...errors,
            ...overrideFormErrors,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        errors.salesPrice,
        errors.purchasePrice,
        errors.amount,
        errors.unit,
        errors.purchaseTaxClassId,
        errors.salesTaxClassId,
        errors.serviceDate,
        overrideFormErrors,
    ]);

    useFulfillmentSectionsErrors(enrichHookFormErrorForFulfillmentSectionErrors({ errors, setFocus }));

    useSectionsValidity(getProductSectionName(product.id), isValid);

    if (!initialImmutableOrderQuote || !taxClasses) {
        return;
    }

    const currencyPerUnitOrdered = `${getCurrencySymbol(orderingOrganization.market.currencyCode)}\u00A0/\u00A0${t(
        SUPPORTED_UNIT_LABEL[initialImmutableOrderQuote.unit]
    )}`;

    const currencyPerUnit = `${getCurrencySymbol(orderingOrganization.market.currencyCode)}\u00A0/\u00A0${t(
        SUPPORTED_UNIT_LABEL[getValues('unit')]
    )}`;

    const formattedTaxClasses = taxClasses.map(taxClass => {
        return {
            value: taxClass.name,
            label: `${taxClass.rate}%`,
            description: taxClass.rate === 0 ? `${taxClass.name} ${taxClass.description}` : '',
        };
    });

    return (
        <form className="focus-within:bg-hovered px-4 py-8">
            <p className="font-copy-sm-strong col-start-2 uppercase [counter-increment:productCounter] before:content-[counter(productCounter)'.\0020']">
                {t(PRODUCT_CATEGORY_LABEL[product.category])}
            </p>
            <div className="mt-4 grid grid-cols-[repeat(5,minmax(0,1fr))] gap-4">
                <div className="col-span-2 truncate">
                    <TextField
                        variant="sm"
                        {...register('name')}
                        errorMessage={getFormError('name')?.message}
                        leadingIcon={getProductCategoryIcon(product.category)}
                        disabled={disabled || disabledFields.productName}
                    />
                </div>
                <div className="col-span-3"></div>

                {contextSource === 'order' && (
                    <>
                        <div className="grid grid-cols-5 gap-1">
                            <NumberField
                                className="col-span-3 min-h-12"
                                value={initialImmutableOrderQuote.amount}
                                size="lg"
                                disabled
                                step={0.001}
                                placeholder={t('product.quote.amount')}
                                aria-label={t('product.quote.amount')}
                            />
                            <Combobox
                                className="col-span-2"
                                disabled
                                variant="sm"
                                options={supportedUnitOptions}
                                value={initialImmutableOrderQuote.unit}
                                onChange={() => {}}
                            />
                        </div>
                        <div className="grid grid-cols-1 gap-0">
                            <TextField
                                disabled
                                variant="sm"
                                value={partnerOrganizationName ?? ''}
                                onChange={() => {}}
                            />
                        </div>
                        <div className="grid grid-cols-5 gap-1">
                            <NumberField
                                disabled
                                size="lg"
                                className="col-span-3 min-h-12"
                                value={initialImmutableOrderQuote.purchasePrice.toNumber()}
                                placeholder={t('product.quote.purchasePrice')}
                                aria-label={t('product.quote.purchasePrice')}
                                trailingSlot={currencyPerUnitOrdered}
                                step={0.01}
                            />
                            <Combobox
                                disabled
                                className="col-span-2"
                                variant="sm"
                                value={initialImmutableOrderQuote.purchaseTaxClassId}
                                onChange={() => {}}
                                options={formattedTaxClasses}
                            />
                        </div>
                        <div className="grid grid-cols-5 gap-1">
                            <NumberField
                                disabled
                                size="lg"
                                className="col-span-3 min-h-12"
                                placeholder={t('product.quote.salesPrice')}
                                aria-label={t('product.quote.salesPrice')}
                                value={initialImmutableOrderQuote.salesPrice.toNumber()}
                                trailingSlot={currencyPerUnitOrdered}
                                step={0.01}
                            />
                            <Combobox
                                disabled
                                className="col-span-2"
                                variant="sm"
                                value={initialImmutableOrderQuote.salesTaxClassId}
                                onChange={() => {}}
                                options={formattedTaxClasses}
                            />
                        </div>
                        <DatePickerField
                            mode="single"
                            disabled
                            size="lg"
                            placeholder={t('product.quote.serviceDate')}
                            selected={new Date(initialImmutableOrderQuote.serviceDate)}
                        />
                    </>
                )}

                <div className="grid grid-cols-5 gap-1">
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <NumberField
                                {...field}
                                size="lg"
                                className="col-span-3 min-h-12 w-full"
                                value={value}
                                placeholder={t('product.quote.amount')}
                                aria-label={t('product.quote.amount')}
                                step={0.001}
                                onValueChange={onChange}
                                disabled={
                                    disabled ||
                                    disabledFields.amount ||
                                    (groupType === PRODUCT_GROUP_TYPES.FRANCO && product.category === 'TRANSPORT')
                                }
                                error={getFormError('amount')?.message}
                            />
                        )}
                    />
                    <Controller
                        name="unit"
                        control={control}
                        render={({ field: { ref: _, ...field } }) => {
                            return (
                                <Combobox
                                    {...field}
                                    className="col-span-2"
                                    disabled={
                                        disabled ||
                                        disabledFields.unit ||
                                        (groupType === PRODUCT_GROUP_TYPES.FRANCO && product.category === 'TRANSPORT')
                                    }
                                    options={supportedUnitOptions}
                                    placeholder={t('product.quote.unit')}
                                    variant="sm"
                                    optionsClassName="min-w-[200px]"
                                />
                            );
                        }}
                    />
                </div>

                {contextSource === 'order' && (
                    <TextField
                        className=""
                        disabled
                        variant="sm"
                        value={partnerOrganizationName ?? ''}
                        onChange={() => {}}
                    />
                )}

                {contextSource === 'position' && (
                    <Controller
                        name="partnerOrganizationId"
                        control={control}
                        render={({ field, fieldState }) => (
                            <SelectOrganization
                                {...field}
                                onChange={value => {
                                    if (!value) return;
                                    field.onChange(value);
                                }}
                                selectedOrganizationId={field.value}
                                placeholder={t('product.addPosition.partner.label')}
                                filters={{
                                    isActive: true,
                                    isBlocked: false,
                                    status: ['approved'],
                                }}
                                critical={!!fieldState.error}
                                helperText={
                                    fieldState.error?.message ?? t('product.addPosition.errorMessage.pleaseEnterValue')
                                }
                                disabled={disabledFields.partnerOrganizationId || disabled}
                                size="sm"
                                excludeMatchingIds={{ id: orderingOrganization.id }}
                                excludeIds={EXCLUDED_PARTNER_ORGANIZATION_IDS}
                                dataTest={`order-product-partner-organization-${product.category}`}
                            />
                        )}
                    />
                )}

                <div className="grid grid-cols-5 gap-1">
                    <Controller
                        name="purchasePrice"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <NumberField
                                {...field}
                                size="lg"
                                error={getFormError('purchasePrice')?.message}
                                className="col-span-3 min-h-12 w-full"
                                value={value.length ? Number(value) : undefined}
                                placeholder={t('product.quote.purchasePrice')}
                                aria-label={t('product.quote.purchasePrice')}
                                trailingSlot={currencyPerUnit}
                                onValueChange={value => {
                                    // todo: the form should use a string for this value
                                    onChange(value === undefined ? '' : value.toString(10));
                                }}
                                disabled={disabled || disabledFields.purchasePrice}
                                step={0.01}
                            />
                        )}
                    />
                    <Controller
                        name="purchaseTaxClassId"
                        control={control}
                        render={({ field: { ref: _, ...field } }) => (
                            <Combobox
                                {...field}
                                className="col-span-2"
                                variant="sm"
                                options={formattedTaxClasses}
                                optionsClassName="min-w-[200px]"
                                disabled={disabled || disabledFields.purchaseTaxClassId}
                                errorMessage={getFormError('purchaseTaxClassId')?.message}
                            />
                        )}
                    />
                </div>
                <div className="grid grid-cols-5 gap-1">
                    <Controller
                        name="salesPrice"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <NumberField
                                {...field}
                                size="lg"
                                error={getFormError('salesPrice')?.message}
                                className="col-span-3 min-h-12 w-full"
                                value={value.length ? Number(value) : undefined}
                                placeholder={t('product.quote.salesPrice')}
                                aria-label={t('product.quote.salesPrice')}
                                trailingSlot={currencyPerUnit}
                                onValueChange={value => {
                                    // todo: the form should use a string for this value
                                    onChange(value === undefined ? '' : value.toString(10));
                                }}
                                disabled={disabled || disabledFields.salesPrice}
                                step={0.01}
                            />
                        )}
                    />
                    <Controller
                        name="salesTaxClassId"
                        control={control}
                        render={({ field: { ref: _, ...field } }) => (
                            <Combobox
                                {...field}
                                className="col-span-2"
                                variant="sm"
                                options={formattedTaxClasses}
                                optionsClassName="min-w-[200px]"
                                disabled={disabled || disabledFields.salesTaxClassId}
                                errorMessage={getFormError('salesTaxClassId')?.message}
                            />
                        )}
                    />
                </div>
                <div>
                    <Controller
                        name="serviceDate"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            const { minDate, maxDate } = getLimitedDateRanges();

                            return isLineItemListingActive && isServiceDateRange ? (
                                <DatePickerField
                                    mode="range"
                                    selected={serviceDateRangeForDatePicker}
                                    placeholder={t('product.quote.serviceDate')}
                                    size="lg"
                                    disabled={disabled || disabledFields.serviceDate}
                                />
                            ) : (
                                <DatePickerField
                                    mode="single"
                                    size="lg"
                                    selected={new Date(value)}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onSelect={date => {
                                        const value = date ? formatDateTimezoneOffsetHotfix(date) : undefined;
                                        onChange(value);
                                    }}
                                    placeholder={t('product.quote.serviceDate')}
                                    disabled={disabled || disabledFields.serviceDate}
                                    error={getFormError('serviceDate')?.message}
                                />
                            );
                        }}
                    />
                </div>
            </div>
            {getValues().amount === 0 && (
                <p className={cn('font-copy-sm mt-4', !isLineItemListingActive && 'text-critical')}>
                    {t('product.fulfillment.productSelection.amountZeroWarning')}
                </p>
            )}
            <CustomRequestProductHints
                isZeroPurchasePrice={!disableZeroPriceHints && Number(getValues('purchasePrice')) === 0}
                isZeroSalesPrice={!disableZeroPriceHints && Number(getValues('salesPrice')) === 0}
                productConfiguration={productConfiguration}
                onUpdateProductConfiguration={productConfiguration => {
                    updateOrderFulfillmentProduct({
                        id: product.id,
                        fulfillmentProductConfiguration: {
                            showZeroPurchasePrice:
                                productConfiguration.showZeroPurchasePrice ??
                                product.fulfillmentProductConfiguration.showZeroPurchasePrice,
                            showZeroSalesPrice:
                                productConfiguration.showZeroSalesPrice ??
                                product.fulfillmentProductConfiguration.showZeroSalesPrice,
                        },
                    });
                    return onUpdateProductConfiguration?.(productConfiguration);
                }}
                orderingOrganizationName={orderingOrganization.name}
                partnerOrganizationName={partnerOrganizationName}
                isNegativePurchasePrice={false}
                isNegativeSalesPrice={false}
                showProductPriceAddedHint={false}
                showProductPriceNotAddedHint={false}
                showZeroPriceProductConfiguration={true}
            />
            {!!hint && <p className="font-copy-sm text-disabled mt-4">{hint}</p>}
            <ReactHookFormDevTool control={control} />
        </form>
    );
};
